import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import CalendarIcon from '../../Assets/images/clinetPageCalendar.svg';
import "react-datepicker/dist/react-datepicker.css";
import styles from './CustomDatePicker.module.css';
import SelectArrow from '../../Assets/images/select-arrow.svg';
import LeftArrow from '../../Assets/images/left-arrow.svg';
import RightArrow from '../../Assets/images/right-arrow.svg';

const CustomDatePicker = ({ 
    selected, 
    onChange, 
    placeholderText, 
    className, 
    disabled,
    showYearDropdown,
    showMonthDropdown,
    dropdownMode,
    yearDropdownItemNumber,
    scrollableYearDropdown,
    maxDate,
    minDate,
    openToDate,
    dateFormat,
    ...props 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef(null);

    const handleInputClick = () => {
        if (!disabled) {
            setIsOpen(true);
        }
    };

    const handleDateChange = (date) => {
        onChange(date);
        setIsOpen(false);
    };

    const handleClickOutside = () => {
        setIsOpen(false);
    };

    // Format date to match DatePicker format
    const formatDateForDisplay = (date) => {
        if (!date) return '';
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear().toString();
        return `${day} ${month} ${year}`;
    };

    const CustomHeader = ({
        date,
        changeYear,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }) => {
        const [isYearOpen, setIsYearOpen] = useState(false);
        const dropdownRef = useRef(null);
        const yearListRef = useRef(null);

        useEffect(() => {
            const handleClickOutsideYear = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsYearOpen(false);
                }
            };
            
            if (isYearOpen) {
                document.addEventListener('mousedown', handleClickOutsideYear);
                // Manual scroll to avoid page jumping with scrollIntoView
                if (yearListRef.current) {
                    // Use setTimeout to ensure render is complete
                    setTimeout(() => {
                        const dropdown = yearListRef.current;
                        if (!dropdown) return;
                        
                        const selectedOption = dropdown.querySelector('[data-selected="true"]');
                        if (selectedOption) {
                            const offsetTop = selectedOption.offsetTop;
                            const optionHeight = selectedOption.offsetHeight;
                            const dropdownHeight = dropdown.offsetHeight;
                            
                            dropdown.scrollTop = offsetTop - (dropdownHeight / 2) + (optionHeight / 2);
                        }
                    }, 0);
                }
            }
            return () => {
                document.removeEventListener('mousedown', handleClickOutsideYear);
            };
        }, [isYearOpen]);

        const startYear = minDate ? minDate.getFullYear() : 1900;
        const endYear = maxDate ? maxDate.getFullYear() : 2100;
        const years = [];
        for (let i = startYear; i <= endYear; i++) {
            years.push(i);
        }

        return (
            <div className={styles.headerContainer}>
                <button 
                    onClick={decreaseMonth} 
                    disabled={prevMonthButtonDisabled}
                    type="button"
                    className={`${styles.arrowButton} ${styles.arrowButtonLeft}`}
                >
                    <img src={LeftArrow} alt="Previous Month" className={styles.navArrowIcon} />
                </button>
                
                <div className={styles.monthYearContainer}>
                    <span>{date.toLocaleString("default", { month: "long" })}</span>
                    
                    {showYearDropdown ? (
                        <div style={{ position: 'relative' }} ref={dropdownRef}>
                            <div 
                                className={styles.yearSelector} 
                                onClick={() => setIsYearOpen(!isYearOpen)}
                            >
                                {date.getFullYear()}
                                <img 
                                    src={SelectArrow} 
                                    alt="v" 
                                    className={`${styles.arrowIcon} ${isYearOpen ? styles.arrowIconOpen : ''}`} 
                                />
                            </div>
                            {isYearOpen && (
                                <div className={styles.yearDropdown} ref={yearListRef}>
                                    {years.map(year => (
                                        <div 
                                            key={year} 
                                            data-selected={date.getFullYear() === year}
                                            className={`${styles.yearOption} ${date.getFullYear() === year ? styles.yearOptionSelected : ''}`}
                                            onClick={() => {
                                                changeYear(year);
                                                setIsYearOpen(false);
                                            }}
                                        >
                                            {year}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <span>{date.getFullYear()}</span>
                    )}
                </div>

                <button 
                    onClick={increaseMonth} 
                    disabled={nextMonthButtonDisabled}
                    type="button"
                    className={`${styles.arrowButton} ${styles.arrowButtonRight}`}
                >
                    <img src={RightArrow} alt="Next Month" className={styles.navArrowIcon} />
                </button>
            </div>
        );
    };

    return (
        <div className="custom-datepicker-wrapper" style={{ position: 'relative' }}>
            <div 
                ref={inputRef}
                onClick={handleInputClick}
                style={{ position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
                <input
                    type="text"
                    value={formatDateForDisplay(selected)}
                    placeholder={placeholderText}
                    className={className}
                    disabled={disabled}
                    readOnly
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                />
                <img
                    src={CalendarIcon}
                    alt="calendar"
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        height: '16px',
                        width: '16px',
                        pointerEvents: 'none'
                    }}
                />
            </div>
            
            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '-270px', // Position above the input
                    left: 0,
                    zIndex: 9999,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    fontFamily: 'inherit'
                }}>
                    <style>
                        {`
                            .custom-datepicker-wrapper .react-datepicker {
                                font-family: inherit !important;
                                border: none !important;
                                background: white !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__header {
                                background-color: white !important;
                                border-bottom: 1px solid #e5e7eb !important;
                                padding: 8px 0 !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__current-month {
                                font-size: 14px !important;
                                font-weight: 600 !important;
                                color: #111827 !important;
                                margin-bottom: 8px !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__day-names {
                                display: grid !important;
                                grid-template-columns: repeat(7, 1fr) !important;
                                gap: 0 !important;
                                margin: 0 !important;
                                padding: 0 8px !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__day-name {
                                width: 32px !important;
                                height: 32px !important;
                                line-height: 32px !important;
                                text-align: center !important;
                                font-size: 12px !important;
                                font-weight: 500 !important;
                                color: #6b7280 !important;
                                margin: 0 !important;
                                padding: 0 !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__month {
                                margin: 0 !important;
                                padding: 8px !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__week {
                                display: grid !important;
                                grid-template-columns: repeat(7, 1fr) !important;
                                gap: 0 !important;
                                margin: 0 !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__day {
                                width: 32px !important;
                                height: 32px !important;
                                line-height: 32px !important;
                                text-align: center !important;
                                font-size: 14px !important;
                                font-weight: 500 !important;
                                color: #111827 !important;
                                margin: 0 !important;
                                padding: 0 !important;
                                border-radius: 4px !important;
                                cursor: pointer !important;
                                transition: all 0.2s !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__day:hover {
                                background-color: var(--primary-100) !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__day--selected {
                                background-color: var(--primary-800) !important;
                                color: white !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__day--keyboard-selected {
                                background-color: var(--primary-800) !important;
                                color: white !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__day--outside-month {
                                color: #d1d5db !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__day--today {
                                font-weight: 600 !important;
                                color: #3b82f6 !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__navigation {
                                top: 8px !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__navigation--previous {
                                left: 8px !important;
                            }
                            .custom-datepicker-wrapper .react-datepicker__navigation--next {
                                right: 8px !important;
                            }
                        `}
                    </style>
            <DatePicker
                        selected={selected}
                        onChange={handleDateChange}
                        onClickOutside={handleClickOutside}
                        open={true}
                        dateFormat={dateFormat || "dd MMM yy"}
                        showYearDropdown={showYearDropdown}
                        showMonthDropdown={showMonthDropdown}
                        dropdownMode={dropdownMode}
                        yearDropdownItemNumber={yearDropdownItemNumber}
                        scrollableYearDropdown={scrollableYearDropdown}
                        maxDate={maxDate}
                        minDate={minDate}
                        openToDate={openToDate}
                        inline
                        renderCustomHeader={(headerProps) => <CustomHeader {...headerProps} />}
                        {...props}
                    />
                </div>
            )}
        </div>
    );
};

export default CustomDatePicker;
